import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppShell } from '~/components/layout/app-shell'
import { ChatPanel } from '~/components/chat/chat-panel'
import { ChatHistorySidebar } from '~/components/chat/chat-history-sidebar'
import {
    deleteChatSession,
    getChatMessages,
    listChatSessions,
} from '~/api/chat-history'
import { getActiveBusinessContext } from '~/api/business-context'
import type { ChatSessionSummary, StoredChatMessage } from '~/api/types'
import type { ChatMessage } from '~/hooks/use-chat-stream'
import { useAuthStore } from '~/stores/auth-store'

/**
 * 상담 에이전트 스킬 칩. 칩을 누르면 해당 스킬로 상담이 시작된다.
 * 일부 칩은 사용자의 저장된 업종에 맞춰 개인화한다(없으면 일반 문구).
 */
function buildSkillPrompts(
    industry?: string | null
): { label: string; message: string }[] {
    const trimmed = industry?.trim()
    // 간이 자가진단: 업종이 있으면 그 업종 기준으로, 없으면 업종을 되묻도록 유도.
    const selfCheckMessage = trimmed
        ? `${trimmed} 사업장의 위생·안전관리 준비 상태를 간단히 자가진단하고 싶어요.`
        : '우리 업종에 맞춰 위생·안전관리 준비 상태를 간단히 자가진단하고 싶어요. 먼저 어떤 업종인지 확인해 주세요.'
    return [
        {
            label: '규모 판정',
            message: '우리가 소규모인지 일반인지 모르겠어요. 판정해 주세요.',
        },
        {
            label: '업종 식별',
            message: '우리 적용대상 업종이 뭔지 모르겠어요. 찾는 걸 도와주세요.',
        },
        {
            label: '간이 자가진단',
            message: selfCheckMessage,
        },
        {
            // 어떤 항목·조문이 궁금한지 에이전트가 먼저 되묻도록 유도한다.
            label: '항목·조문 해설',
            message:
                '평가 항목이나 고시 조문 해설을 받고 싶어요. 어떤 항목·조문이 궁금한지 먼저 물어봐 주세요.',
        },
        {
            label: '인증 받기 상담',
            message: 'HACCP 인증을 받고 싶어요. 무엇부터 준비해야 하나요?',
        },
        {
            label: '플랫폼 사용법',
            message:
                'HACCP AI 플랫폼을 처음 써봐요. 어떤 기능이 있고 무엇부터 하면 되는지 알려주세요.',
        },
    ]
}

/** 저장된 메시지(DTO)를 채팅 패널이 쓰는 ChatMessage로 변환한다. */
function toChatMessages(stored: StoredChatMessage[]): ChatMessage[] {
    return stored.map((m, i) => ({
        id: `${m.createdAt}-${i}`,
        role: m.role,
        content: m.content,
        // 저장된 근거를 보존해 세션 복원 시 우측 근거 패널을 재현한다.
        citations: m.citations,
    }))
}

/** 상담 대화(근거 카드). consultation 프로파일. 좌측에 상담 히스토리 사이드바. */
export function ConsultationChatPage() {
    const isAuthenticated = useAuthStore((s) => s.status === 'authenticated')

    const [searchParams, setSearchParams] = useSearchParams()

    const [sessions, setSessions] = useState<ChatSessionSummary[]>([])
    const [loading, setLoading] = useState(false)
    // 저장된 사업장 업종(간이 자가진단 칩 개인화용). 없으면 일반 문구.
    const [industry, setIndustry] = useState<string | null>(null)
    // 선택된 세션. null이면 새 상담(시드 없음).
    const [selected, setSelected] = useState<ChatSessionSummary | null>(null)
    const [initialMessages, setInitialMessages] = useState<ChatMessage[]>([])

    // 홈 HERO에서 넘어온 시드 질문(?seed=…). 마운트 시 한 번만 잡아 자동 전송한다.
    // ref 가드로 새로고침/재마운트 중복 전송을 막고, 즉시 쿼리를 비운다.
    const seedRef = useRef<string | null>(null)
    if (seedRef.current === null) {
        seedRef.current = searchParams.get('seed') ?? ''
    }
    const seed = seedRef.current || undefined

    useEffect(() => {
        if (searchParams.has('seed')) {
            const next = new URLSearchParams(searchParams)
            next.delete('seed')
            setSearchParams(next, { replace: true })
        }
        // 마운트 시 1회만: 시드는 seedRef에 이미 캡처돼 있다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const refreshSessions = useCallback(() => {
        if (!isAuthenticated) return
        setLoading(true)
        listChatSessions('consultation')
            .then(setSessions)
            .catch(() => setSessions([]))
            .finally(() => setLoading(false))
    }, [isAuthenticated])

    // 마운트/로그인 시 세션 목록 로드.
    useEffect(() => {
        refreshSessions()
    }, [refreshSessions])

    // 저장된 사업장 업종을 불러와 자가진단 칩을 개인화한다(실패해도 무시).
    useEffect(() => {
        if (!isAuthenticated) return
        getActiveBusinessContext()
            .then((ctx) => setIndustry(ctx?.industry ?? null))
            .catch(() => {})
    }, [isAuthenticated])

    const handleSelect = useCallback(async (session: ChatSessionSummary) => {
        try {
            const stored = await getChatMessages(session.id)
            setInitialMessages(toChatMessages(stored))
        } catch {
            setInitialMessages([])
        }
        setSelected(session)
    }, [])

    const handleNew = useCallback(() => {
        setSelected(null)
        setInitialMessages([])
    }, [])

    // 세션 삭제: 서버에서 지운 뒤 목록에서 제거하고, 지운 세션이 열려 있었다면 새 상담으로 되돌린다.
    const handleDelete = useCallback(
        async (session: ChatSessionSummary) => {
            const prev = sessions
            // 낙관적 제거로 즉시 반응. 실패하면 되돌린다.
            setSessions((list) => list.filter((s) => s.id !== session.id))
            if (selected?.id === session.id) {
                setSelected(null)
                setInitialMessages([])
            }
            try {
                await deleteChatSession(session.id)
            } catch {
                setSessions(prev)
            }
        },
        [sessions, selected]
    )

    return (
        <AppShell>
            <div className='mx-auto flex h-full w-full gap-4 px-4 py-6 lg:px-8'>
                <ChatHistorySidebar
                    sessions={sessions}
                    activeSessionId={selected?.id ?? null}
                    onSelect={handleSelect}
                    onNew={handleNew}
                    onDelete={handleDelete}
                    isAuthenticated={isAuthenticated}
                    loading={loading}
                />

                <div className='flex min-w-0 flex-1 flex-col'>
                    <h1 className='text-foreground mb-4 text-lg font-semibold'>
                        HACCP AI 상담챗봇
                    </h1>
                    <div className='min-h-0 flex-1'>
                        <ChatPanel
                            profile='consultation'
                            sessionId={selected?.threadId}
                            initialMessages={initialMessages}
                            autoSendMessage={selected ? undefined : seed}
                            onSessionId={refreshSessions}
                            placeholder='HACCP 규정 관련 질문을 입력하세요'
                            emptyTitle='HACCP에 대해 무엇이든 물어보세요'
                            emptyDescription='아래 스킬을 누르거나, HACCP 규정집 근거로 답을 받아보세요.'
                            quickPrompts={buildSkillPrompts(industry)}
                        />
                    </div>
                </div>
            </div>
        </AppShell>
    )
}
